/**
 * Icon component module.
 * @module shared/components/Icon
 */
import React from 'react';
import SVG from 'react-inlinesvg';
import PropTypes from 'prop-types';
import styled, {withTheme, css} from 'styled-components/macro';
import Popover from './Popover';
import {PORTAL_NAME, WIDGETS_DOMAIN} from '../../config.json';

const basePath = ((PORTAL_NAME === 'widgets') && WIDGETS_DOMAIN) || '';

export const icons = {
  cbSymbol: 'https://media.circleblack.com/cb/CB_Investor_Symbol.png',
  dotNew: 'https://media.circleblack.com/icons/Status/Other/status-other-new.svg',
  dotUpdated: 'https://media.circleblack.com/icons/Status/Other/status-other-updated.svg',
  driftDataIssues: 'https://media.circleblack.com/icons/Status/Drift/status-drift-data_issue.svg',
  driftDrifted: 'https://media.circleblack.com/icons/Status/Drift/status-drift-drifted.svg',
  driftNoModel: 'https://media.circleblack.com/icons/Status/Drift/status-drift-no_model.svg',
  driftNotApplicable: 'https://media.circleblack.com/icons/Status/Drift/status-drift-not_applicable.svg',
  driftOnTarget: 'https://media.circleblack.com/icons/Status/Drift/status-drift-on_target.svg',
  driftRecentProposal: 'https://media.circleblack.com/icons/Status/Drift/status-drift-recent_proposal.svg',
  driftRecentRequest: 'https://media.circleblack.com/icons/Status/Drift/status-drift-recent_request.svg',
  menuBars: 'https://media.circleblack.com/icons/Menu_Icon.png',
  menuBarsGradient: 'https://media.circleblack.com/icons/Menu_Icon_Gradient.png',
  performance: 'https://media.circleblack.com/icons/Performance_Icon.svg',
  exposure: 'https://media.circleblack.com/icons/Exposure_Icon.svg',
  wealthalerts: 'https://media.circleblack.com/icons/Wealth_Alerts_Icon.svg',
  insights: 'https://media.circleblack.com/icons/Insights_Icon.svg',
  goals: 'https://media.circleblack.com/icons/Goals_Icon.svg',
  accountGroups: 'https://media.circleblack.com/icons/ADVISOR_GROUPs_Icon.svg',
  holdings: 'https://media.circleblack.com/icons/Holdings_Icon.svg',
  transactions: 'https://media.circleblack.com/icons/Transactions_Icon.svg',
  profile: 'https://media.circleblack.com/icons/Profile_Icon.svg',
  manageAccounts: 'https://media.circleblack.com/icons/Manage_Accounts_Icon.svg',
  reports: 'https://media.circleblack.com/icons/Reports_Icon.svg',
  notification: 'https://media.circleblack.com/icons/notification_bell.svg',
  preferences: 'https://media.circleblack.com/icons/Preferences_Icon.svg',
  preferencesRing: 'https://media.circleblack.com/icons/Preferences_Ring_Icon.svg',
  feedback: 'https://media.circleblack.com/icons/Feedback_Icon.svg',
  reportIssue: 'https://media.circleblack.com/icons/Report_An_Issue_Icon.svg',
  questionMarkCircle: 'https://media.circleblack.com/icons/question-mark-3.svg',
  logout: 'https://media.circleblack.com/icons/Logout_Icon.svg',
  changeUp: 'https://media.circleblack.com/icons/delta-up.svg',
  changeDown: 'https://media.circleblack.com/icons/delta-down.svg',
  changeZero: 'https://media.circleblack.com/icons/delta-zero.svg',
  arrow: 'https://media.circleblack.com/icons/selector-arrow.svg',
  arrowCircle: {svg: basePath + '/icons/pagination_right_icon.svg', type: 'standard'},
  help: {svg: basePath + '/icons/help_icon.svg', type: 'standard'},
  alertCircle: 'https://media.circleblack.com/icons/alert.svg',
  checkMarkCircle: 'https://media.circleblack.com/icons/on-target.svg',
  checkMarkCircleO: 'https://media.circleblack.com/icons/check-circle-o.svg',
  checkMarkCircleInfo: 'https://media.circleblack.com/icons/Selective_Approved.svg',
  hourglass: 'https://media.circleblack.com/icons/Pending_Approval.svg',
  lock: 'https://media.circleblack.com/icons/lock_icon.png',
  magnifyingGlass: 'https://media.circleblack.com/icons/magnifying-glass.svg',
  remove: {svg: basePath + '/icons/remove_icon.svg', type: 'standard'},
  close: {svg: basePath + '/icons/close_icon.svg', type: 'standard'},
  cbCircle: 'https://media.circleblack.com/icons/filled-circle.svg',
  cbCircleO: 'https://media.circleblack.com/icons/empty-circle.svg',
  notAllowed: basePath + '/icons/no_circle_icon.svg',
  envelope: 'https://media.circleblack.com/icons/envelope.svg',
  carrot: 'https://media.circleblack.com/icons/carrot.svg',
  pullTab: 'https://media.circleblack.com/icons/pull-tab.svg',
  threeDot: {svg: '/icons/three_dot.svg', type: 'standard'},
  mainMenuExit: 'https://media.circleblack.com/icons/main-menu-exit.svg',
  circlePlus: 'https://media.circleblack.com/icons/plus-circle.svg',
  circleMinus: 'https://media.circleblack.com/icons/minus-circle.svg',
  checkbox: {svg: basePath + '/icons/checkbox_icon.svg', type: 'checkbox'},
  // boxUnchecked: {svg: '/icons/checkbox_unchecked.svg', type: 'checkbox'},
  boxCombo: 'https://media.circleblack.com/icons/Checkbox_Combo_Icon.svg',
  radio: {svg: basePath + '/icons/radio_icon.svg', type: 'radio'},
  // radioSelected: 'https://media.circleblack.com/icons/Radio_Button_Selected.svg', // ETODO: Remove this once new icon Radio_Button_Selected_2.svg is uploaded to media.
  // radioSelected2: 'https://media.circleblack.com/icons/Radio_Button_Selected_2.svg',
  // radioUnselected: 'https://media.circleblack.com/icons/Radio_Button_Unselected.svg',
  gear: {svg: basePath + '/icons/gear_icon.svg', type: 'standard'},
  favorite: 'https://media.circleblack.com/icons/favorite.svg',
  preferred: 'https://media.circleblack.com/icons/Preferred.svg',
  report: 'https://media.circleblack.com/icons/report.svg',
  contextAccounts: 'https://media.circleblack.com/icons/context-accounts.svg',
  clock: 'https://media.circleblack.com/icons/clock.svg',
  accounts: 'https://media.circleblack.com/icons/accounts.svg',
  home: 'https://media.circleblack.com/icons/home_icon.svg',
  overviewMenu: 'https://media.circleblack.com/icons/overview_icon.svg',
  clientCircle: 'https://media.circleblack.com/icons/Client_Selection_Icon.svg',
  client: {svg: '/icons/client_icon.svg', type: 'standard'},
  clients: 'https://media.circleblack.com/icons/clients.svg',
  accountCircle: 'https://media.circleblack.com/icons/Account_Selection_Icon.svg',
  clientsCircle: 'https://media.circleblack.com/icons/Household_Selection_Icon.svg',
  advisors: 'https://media.circleblack.com/icons/advisors.svg',
  advisorsCircle: 'https://media.circleblack.com/icons/Advisor_Circle_Icon_v2.svg',
  branches: 'https://media.circleblack.com/icons/branches.svg',
  sidebarChevron: 'https://media.circleblack.com/icons/sidebar-chevron.svg',
  shield: 'https://media.circleblack.com/misc/shield_large.svg',
  exclamationCircle: 'https://media.circleblack.com/icons/exclamation-circle.svg',
  dataCurrent: 'https://media.circleblack.com/icons/data-current.svg',
  delayOfFeed: 'https://media.circleblack.com/icons/feed-delayed.svg',
  notAggregating: 'https://media.circleblack.com/icons/aggregation-issues.svg',
  notApplicable: 'https://media.circleblack.com/icons/Status/Drift/status-drift-not_applicable.svg',
  slideArrowLeft: 'https://media.circleblack.com/icons/Slide_Left_Arrow_Icon.svg',
  slideArrowRight: 'https://media.circleblack.com/icons/Slide_Right_Arrow_Icon.svg',
  stockCalc: 'https://media.circleblack.com/partners/stockCalcLogo.svg',
  ip55: 'https://media.circleblack.com/partners/55ipLogoWhiteHorizontal.svg',
  circleAccept: 'https://media.circleblack.com/icons/Accept_Icon.svg',
  circleCancel: 'https://media.circleblack.com/icons/Cancel_Icon.svg',
  circleAdd: 'https://media.circleblack.com/icons/Add_Icon.svg',
  circleDelete: 'https://media.circleblack.com/icons/Delete_Icon.svg',
  circleDismiss: 'https://media.circleblack.com/icons/Dismiss_Icon.svg',
  circleEdit: 'https://media.circleblack.com/icons/edit.svg',
  circleSchedule: 'https://media.circleblack.com/icons/Schedule_Icon.svg',
  circleShare: 'https://media.circleblack.com/icons/Circle_Share_Icon.svg',
  attention: {svg: basePath + '/icons/attention_icon.svg', type: 'colored'},
  warning: 'https://media.circleblack.com/icons/warning_icon.png',
  warningAlt: 'https://media.circleblack.com/icons/warning_alt_icon.png',
  calendar: {svg: basePath + '/icons/calendar_icon.svg', type: 'standard'},
  details: {svg: basePath + '/icons/details_icon.svg', type: 'standard'},
  maximize: {svg: basePath + '/icons/maximize_icon.svg', type: 'standard'},
  more: {svg: basePath + '/icons/more_icon.svg', type: 'standard'},
  thumbsUp: {svg: basePath + '/icons/thumbsup_icon.svg', type: 'standard'},
  filter: {svg: basePath + '/icons/filter_icon.svg', type: 'filter'},
  issueAccountDiscrepancy: 'https://media.circleblack.com/icons/Account_Discrepancy_Icon.svg',
  issueNoBalance: 'https://media.circleblack.com/icons/No_Balance_Icon.svg',
  issueNegativeBalance: 'https://media.circleblack.com/icons/Negative_Balance_Icon.svg',
  wealthbox: 'https://media.circleblack.com/partners/wealthbox-logo-light.png',
  selectionList: {svg: basePath + '/icons/selection_list_icon.svg', type: 'standard'},
  iconsKey: {svg: basePath + '/icons/icons_key_icon.svg', type: 'standard'},
  mainMenu: {svg: basePath + '/icons/main_menu_icon.svg', type: 'standard'},
  notifications: {svg: basePath + '/icons/notifications_icon.svg', type: 'standard'},
  tileMove: {svg: basePath + '/icons/tile_move_icon.svg', type: 'standard'},
  tileResize: {svg: basePath + '/icons/tile_resize_icon.svg', type: 'standard'},
  tileHide: {svg: basePath + '/icons/tile_hide_icon.svg', type: 'standard'},
  reOrder: {svg: basePath + '/icons/reorder_icon.svg', type: 'standard'},
  goToIconSmall: 'https://media.circleblack.com/icons/go_to_icon_small.svg',
  noScore: {svg: basePath + '/icons/no_score.svg', type: 'standard'},
  preview: {svg: basePath + '/icons/preview_icon.svg', type: 'standard'},
  download: {svg: basePath + '/icons/download_icon.svg', type: 'standard'},
};

const types = {
  colored: props => `
  & svg #Foreground {
    fill: ${props.fillColor};
  }`,
  checkbox: props => `
  & svg {
    fill: ${props.fillColor};
    ${props.rotate ? `transform: rotate(${props.rotate});` : ''}
    pointer-events: none;
  }

  & svg #Checkmark {
    fill: ${props.theme.primaryColor};
  }

  & svg #Intermediate {
    fill: ${props.theme.primaryColor};
  }

  & svg #Background {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
    transition: opacity 0.3s;
  }

  & svg #Border {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
  }`,
  radio: props => `
  & svg {
    fill: ${props.fillColor};
    ${props.rotate ? `transform: rotate(${props.rotate});` : ''}
    pointer-events: none;
  }

  & svg #Selection {
    fill: ${props.theme.primaryColor};
  }

  & svg #Background {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
    transition: opacity 0.3s;
  }

  & svg #Border {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
  }`,
  standard: props => `
  & svg {
    ${props.rotate ? `transform: rotate(${props.rotate});` : ''}
    pointer-events: none;
  }
  & svg #Background {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
    transition: opacity 0.3s;
  }
  ${!!props.onClick && `&:hover svg #Background {
    opacity: 0.15;
  }`};
  &:hover[disabled] svg #Background {
    opacity: 0;
  }
  & svg #Foreground {
    fill: ${props.theme.primaryColor};
  }

  & svg #Border {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
  }
  `,
  filter: props => `
  & svg {
    ${props.rotate ? `transform: rotate(${props.rotate});` : ''}
    pointer-events: none;
  }
  & svg #Background {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
    transition: opacity 0.3s;
  }
  ${!!props.onClick && `&:hover svg #Background {
    opacity: 0.15;
  }`};
  &:hover[disabled] svg #Background {
    opacity: 0;
  }
  & svg #Foreground {
    fill: ${props.theme.primaryColor};
    opacity: 0;
  }

  & svg #Border {
    fill: ${props.theme.dark ? '#FFFFFF' : '#000000'};
    opacity: 0.8;
  }

  ${!!props.alternate && `
  & svg #Foreground {
    opacity: 1;
  }
  & svg #Border {
    opacity: 0;
  }
  `}
  `,
};

const _IconWrapper = styled.div`
  line-height: 0;
  display: inline-block;
  margin: ${props => props.margin};
  width: ${props => props.width};
  height: ${props => props.height};
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => (props.disabled && 'not-allowed') || (props.isClickable && 'pointer') || 'normal'};

  & svg {
    pointer-events: none;
    fill: ${props => props.fillColor};
    ${props => props.rotate ? `transform: rotate(${props.rotate});` : ''}
    pointer-events: none;
  }

  ${props => {
    if (props.color) {
      return css`
        svg path,
        svg rect {
          fill: ${props.color} !important;
        },        
        svg line,
        svg polygon  {
          stroke: ${props.color} !important;
        }`;
    }
  }}
  
  ${props => types[props.type] && types[props.type](props)};
`;

const Icon = (props) => {
  const icon = icons[props.svg];
  const {
    description,
    isPopoverControlled,
    showPopover,
    height = icon?.type ? '30px' : '20px',
    width = icon?.type ? '30px' : '20px',
    margin = icon?.type ? '0px' : '5px',
    color,
    svg,
    action,
    disabled,
    processing,
    rotate,
    allowEventPropagation,
    allowEventDefault,
    alternate,
    className,
  } = props;
  const [internalProcessing, setInternalProcessing] = React.useState();
  const isDisabled = (disabled || internalProcessing || processing);

  return <Popover
    trigger='hover'
    placement='top'
    content={description}
    isControlled={isPopoverControlled}
    show={showPopover}>
    <_IconWrapper
      className={className}
      width={width}
      rotate={rotate}
      height={height}
      color={color}
      fillColor={color || '#00d0be'}
      margin={margin}
      disabled={isDisabled}
      isClickable={!!action}
      alternate={alternate}
      type={icon?.type}
      onClick={action ? e => {
        if (!allowEventDefault) {
          e.preventDefault();
        }

        if (!allowEventPropagation) {
          e.stopPropagation();
        }

        if (isDisabled) {
          return;
        }
        const promise = action(e);
        if (promise?.then) {
          setInternalProcessing(true);
          promise.finally(() => setInternalProcessing(false));
        }
      } : null}>
      <SVG
        height={height === 'fill' ? '100%' : height}
        width={width === 'fill' ? '100%' : width}
        cacheRequests={true}
        src={icon?.svg || icon || svg}
        title={description}
        description={description}
      />
    </_IconWrapper>
  </Popover>;
};

export default withTheme(Icon);

Icon.propTypes = {
  /**
   * CSS Height, defaults to 20px
   */
  height: PropTypes.string,
  /**
   * CSS Width, defaults to 20px
   */
  width: PropTypes.string,
  /**
   * CSS Margin for wrapper, defaults to 5px
   */
  margin: PropTypes.string,
  /**
   * Fill color for svg, defaults to Theme Primary Color
   */
  color: PropTypes.string,
  /**
   * The popover message/description
   */
  description: PropTypes.string,
  /**
   * Whether popover is controlled from parent
   */
  isPopoverControlled: PropTypes.bool,
  /**
   * Whether to show the popover (if controlled from parent)
   */
  showPopover: PropTypes.bool,
  /**
   * The name of or link to the svg
   */
  svg: PropTypes.oneOfType([PropTypes.oneOf(Object.keys(icons)), PropTypes.string]).isRequired,
  /**
   * Optional OnClick Action
   */
  action: PropTypes.func,
  /**
   * Optionally disabled
   */
  disabled: PropTypes.bool,
  /**
   * Indicate that the icon is processing
   */
  processing: PropTypes.bool,

  /**
   * CSS Rotation
   */
  rotate: PropTypes.string,
  /**
   * If true displays alternate css for the type. for example the filtered state for the filtered icon.
   */
  alternate: PropTypes.bool,
  allowEventPropagation: PropTypes.bool,
  allowEventDefault: PropTypes.bool,
  className: PropTypes.string,
};

Icon.defaultProps = {
  allowEventPropagation: false,
  allowEventDefault: false,
};
